import React, { useRef, useState } from "react";
import { useCart } from "../context/CartContext";
import { PHONE_MODELS, fmtCLP } from "../lib/api";
import { Upload, Sparkles, Check, ImageIcon, Move, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PhoneModelPicker from "./PhoneModelPicker";
import CameraModule, { getCameraLayout } from "./CameraModule";

const BASE_PRICE = 12990;

export default function Customizer({ initialProduct = null, onAdded }) {
  const [imageData, setImageData] = useState(null);
  const [text, setText] = useState("");
  const [model, setModel] = useState(PHONE_MODELS[0]);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const dragRef = useRef(null);
  const fileRef = useRef();
  const { addItem } = useCart();

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 4 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageData(ev.target.result);
      setPos({ x: 0, y: 0 });
      setScale(1);
    };
    reader.readAsDataURL(file);
  };

  // Drag handlers (works for mouse, touch & pen via PointerEvents)
  const onPointerDown = (e) => {
    if (!imageData) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setPos({ x: d.origX + (e.clientX - d.startX), y: d.origY + (e.clientY - d.startY) });
  };
  const onPointerUp = (e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
  };
  const resetPosition = () => {
    setPos({ x: 0, y: 0 });
    setScale(1);
  };

  const onAdd = () => {
    if (!imageData && !text.trim()) {
      toast.error("Sube una imagen o escribe un texto para personalizar");
      return;
    }
    const productName = initialProduct?.name || "Funda Personalizada Premium";
    addItem({
      product_id: initialProduct?.id || "custom-funda",
      name: productName + ` · ${model}`,
      price: initialProduct?.price || BASE_PRICE,
      image: imageData || initialProduct?.images?.[0],
      custom_image: imageData,
      custom_text: text,
      phone_model: model,
    });
    toast.success("¡Funda personalizada agregada al carrito!");
    setImageData(null);
    setText("");
    if (onAdded) onAdded();
  };

  return (
    <section id="personalizar" className="relative py-16 md:py-24 bg-gradient-to-b from-white to-sky-50/40" data-testid="customizer-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#0EA5E9]">Personaliza tu funda</div>
          <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tight text-black mt-2">
            Diseña la tuya en <span className="text-[#0EA5E9]">15 minutos</span>
          </h2>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">Sube una foto, agrega texto y elige tu modelo. Listo.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Preview - Back of phone case */}
          <div className="order-2 lg:order-1">
            <div className="relative max-w-[220px] sm:max-w-[240px] mx-auto" data-testid="customizer-preview">
              {/* Phone case body (back view) - smaller, fits screen */}
              <div className="relative aspect-[9/19] rounded-[2.4rem] bg-slate-900 p-[5px] shadow-2xl tech-glow">
                <div className="relative w-full h-full rounded-[2.05rem] overflow-hidden bg-slate-100">
                  {/* Custom design printed on the back of the case - DRAGGABLE */}
                  {imageData ? (
                    <div
                      className="absolute inset-0 touch-none select-none cursor-grab active:cursor-grabbing"
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                      data-testid="preview-drag-layer"
                    >
                      <img
                        src={imageData}
                        alt="diseño"
                        draggable="false"
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none will-change-transform"
                        style={{ transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`, transformOrigin: "center" }}
                      />
                      {/* Hint shown when at default position */}
                      {pos.x === 0 && pos.y === 0 && scale === 1 && (
                        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center pointer-events-none">
                          <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                            <Move size={11} /> Mueve y ajusta tu diseño
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-white to-sky-200 flex flex-col items-center justify-center text-slate-400 px-4 text-center">
                      <ImageIcon size={36} className="mb-2" />
                      <div className="text-[11px] font-semibold leading-tight">Tu diseño<br />parte trasera</div>
                    </div>
                  )}

                  {/* Glossy reflection overlay (above image, below text) */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/25 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-white/15 rotate-45 blur-2xl pointer-events-none" />

                  {/* Custom text overlay on the back */}
                  {text && (
                    <div className="absolute inset-x-0 bottom-8 px-3 text-center pointer-events-none">
                      <span
                        className="font-heading font-black text-lg text-white"
                        style={{ textShadow: "0 2px 12px rgba(0,0,0,0.7), 0 0 24px rgba(0,0,0,0.4)" }}
                      >
                        {text}
                      </span>
                    </div>
                  )}
                </div>

                {/* Dynamic camera module based on selected model */}
                <CameraModule layout={getCameraLayout(model)} />

                {/* Side buttons (subtle) */}
                <div className="absolute -left-[3px] top-[22%] w-[3px] h-7 bg-slate-700 rounded-l" />
                <div className="absolute -left-[3px] top-[33%] w-[3px] h-12 bg-slate-700 rounded-l" />
                <div className="absolute -right-[3px] top-[28%] w-[3px] h-14 bg-slate-700 rounded-r" />
              </div>

              {/* Soft shadow under phone */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-black/20 blur-2xl rounded-full -z-10" />
            </div>
            <div className="mt-5 flex flex-col items-center gap-2 text-center">
              <div className="inline-flex items-center gap-2 text-xs text-slate-500">
                <Sparkles size={13} className="text-[#0EA5E9]" />
                <span>Vista previa de la funda · {model}</span>
              </div>
              {imageData && (
                <div className="flex items-center gap-3 mt-1">
                  <div className="flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1.5">
                    <span className="text-[10px] uppercase font-bold text-slate-600">Zoom</span>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.05"
                      value={scale}
                      onChange={(e) => setScale(parseFloat(e.target.value))}
                      className="w-24 accent-[#0EA5E9]"
                      data-testid="preview-zoom-slider"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={resetPosition}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-black bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition"
                    data-testid="preview-reset-btn"
                  >
                    <RotateCcw size={12} /> Centrar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="order-1 lg:order-2 bg-white rounded-3xl border border-slate-200 p-6 md:p-8 shadow-sm">
            <Step n={1} label="Sube tu imagen" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="customizer-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-2 w-full border-2 border-dashed border-slate-300 hover:border-[#0EA5E9] hover:bg-sky-50 rounded-2xl py-8 flex flex-col items-center gap-2 text-slate-500 transition"
              data-testid="customizer-upload-btn"
            >
              <Upload size={28} />
              <div className="font-semibold">{imageData ? "Cambiar imagen" : "Click para subir foto"}</div>
              <div className="text-xs">JPG, PNG, WEBP · máx 4MB</div>
            </button>

            <div className="mt-6">
              <Step n={2} label="Agrega un texto (opcional)" />
              <input
                type="text"
                placeholder="Tu nombre o frase favorita"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 30))}
                data-testid="customizer-text-input"
                className="mt-2 w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none font-medium"
              />
            </div>

            <div className="mt-6">
              <Step n={3} label="Modelo de celular" />
              <div className="mt-2">
                <PhoneModelPicker value={model} onChange={setModel} testId="customizer-model-picker" />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                ¿No ves tu modelo? Escríbenos por WhatsApp y lo agregamos.
              </p>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Precio</div>
                <div className="font-heading font-black text-3xl text-black">{fmtCLP(initialProduct?.price || BASE_PRICE)}</div>
              </div>
              <button
                onClick={onAdd}
                data-testid="customizer-add-cart"
                className="btn-primary inline-flex items-center gap-2"
              >
                <Check size={18} /> Agregar al carrito
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Step = ({ n, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-7 h-7 rounded-full bg-black text-white text-sm font-bold flex items-center justify-center">{n}</span>
    <div className="font-heading font-bold text-black">{label}</div>
  </div>
);
